import { useEffect, useState } from 'react';

import { fetchGetMainPageBrandBannerEnrichmentsService } from 'shared/services/fetchGetMainPageBrandBannerEnrichmentsService';
import { type IV1GetMainPageBrandBannerEnrichmentsResponse } from 'shared/repositories/ad-banner/entities/get_main_page_brand_banner_enrichments_v1/V1GetMainPageBrandBannerEnrichmentsResponse';
import { type TMainpageBrandContext } from 'shared/types/bannerContext';
import { useApplicationContext } from 'shared/utils/applicationContext';

const enrichmentsCache = new Map<number, IV1GetMainPageBrandBannerEnrichmentsResponse>();

/**
 * @description Используется для обогащения данных баннера для типа mainpageBrand.
 * Не использует redux для получения и хранения данных, так как данные слишком
 * локальные и не нужны другим компонентам.
 */
export function useMainPageBrandBannerEnrichments({
  objectId,
  objectType,
  dealType,
  useServerEnrichment,
  isApartment,
}: TMainpageBrandContext): null | IV1GetMainPageBrandBannerEnrichmentsResponse {
  const applicationContext = useApplicationContext();

  // Number (в отличие от parseInt) не парсит частично: '12abc' -> NaN, а не 12, поэтому на бэкенд не уйдёт битый id.
  const objectIdNumber = objectId ? Number(objectId) : NaN;
  const objectIdInt = Number.isInteger(objectIdNumber) ? objectIdNumber : null;

  const isEnrichmentFromBackendEnabled = useServerEnrichment === '1';

  const [serverData, setServerData] = useState<null | IV1GetMainPageBrandBannerEnrichmentsResponse>(() =>
    objectIdInt && isEnrichmentFromBackendEnabled ? (enrichmentsCache.get(objectIdInt) ?? null) : null,
  );

  useEffect(() => {
    if (!isEnrichmentFromBackendEnabled || !objectIdInt || serverData) {
      return;
    }

    fetchGetMainPageBrandBannerEnrichmentsService(applicationContext, {
      dealType,
      isApartment: isApartment === '1',
      objectId: objectIdInt,
      objectType,
    })
      .then(data => {
        /**
         * @description не даём кешу расти бесконечно; стратегия хранения для нас не важна,
         * поэтому при переполнении просто чистим его целиком.
         */
        if (enrichmentsCache.size > 100) {
          enrichmentsCache.clear();
        }

        enrichmentsCache.set(objectIdInt, data);

        setServerData(data);
      })
      .catch(() => {
        // Обогащение необязательно: при ошибке баннер рендерится без него, причина уже залогирована в сервисе.
      });
    /**
     * @description изменение данных не должно триггерить запрос
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return serverData;
}
